import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { Field, SelectField } from "@/components/common/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatINRShort } from "@/lib/format";
import {
  UserPlus, ShieldCheck, User as UserIcon, Mail, Phone, ChevronRight, Loader2,
} from "lucide-react";

export default function Team() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [perf, setPerf] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", role: "employee" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/dashboard/team")])
      .then(([u, t]) => {
        setUsers(u.data);
        const map = {};
        t.data.performance.forEach((p) => { map[p.employee_id] = p; });
        setPerf(map);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setForm({ full_name: "", email: "", password: "", phone: "", role: "employee" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add member");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, active: !u.active } : x)));
      toast.success(u.active ? "Deactivated" : "Activated");
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div className="animate-fade-in-up space-y-4">
      <PageHeader title="Team" subtitle="Manage employees & access"
        actions={<Button variant="brand" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">Add Member</span></Button>} />

      {loading ? <Loader /> : (
        <div className="space-y-2.5">
          {users.map((u) => {
            const p = perf[u.id];
            const leader = u.role === "team_leader";
            return (
              <div key={u.id} className="rounded-xl border border-border bg-card p-3.5 shadow-card">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {u.full_name.slice(0, 1)}
                  </span>
                  <button onClick={() => !leader && navigate(`/team/employees/${u.id}`)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">{u.full_name}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${leader ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"}`}>
                        {leader ? <ShieldCheck className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                        {leader ? "Leader" : "Employee"}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
                      {u.phone && <span className="hidden items-center gap-1 sm:inline-flex"><Phone className="h-3 w-3" /> {u.phone}</span>}
                    </div>
                  </button>
                  {!leader && (
                    <div className="flex items-center gap-2">
                      <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} />
                      <button onClick={() => navigate(`/team/employees/${u.id}`)}><ChevronRight className="h-4 w-4 text-muted-foreground" /></button>
                    </div>
                  )}
                </div>
                {!leader && p && (
                  <div className="mt-3 grid grid-cols-4 gap-2 border-t border-border pt-3 text-center">
                    {[["Leads", p.leads_created], ["Deals", p.deals_done], ["Conv.", `${p.conversion}%`], ["Business", formatINRShort(p.business_amount)]].map(([k, v]) => (
                      <div key={k}>
                        <div className="tnum text-sm font-bold text-foreground">{v}</div>
                        <div className="text-[10px] text-muted-foreground">{k}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Full Name"><Input className="h-11" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="e.g. Rahul Sharma" /></Field>
            <Field label="Email"><Input className="h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@rvsf.com" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Password"><Input className="h-11" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" /></Field>
              <Field label="Phone"><Input className="h-11" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile" /></Field>
            </div>
            <Field label="Role">
              <SelectField value={form.role} onChange={(v) => setForm({ ...form, role: v })}
                options={[{ value: "employee", label: "Employee" }, { value: "team_leader", label: "Team Leader" }]} />
            </Field>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="brand" onClick={create} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
